<template>
  <header
    class="TheHeader"
    :class="{ mobileNavOpen }">
    <div
      class="TheHeader_Leading">
      <a
        class="TheHeader_Logo"
        href="https://www.domo.com">
        <img
          v-track-click="{
            position: 'header',
            name: 'logo',
            path: $route.path
            }"
          class="TheHeader_Logo_Domo"
          src="https://web-assets.domo.com/miyagi/images/logo/brand/white/logo-brand-domo-text-white.svg"
          alt="Domo Logo"
        />
      </a>

      <transition
        name="fade">
        <div
          v-show="isMobile && mobileNavOpen"
          class="MobileNav"
          :class="{ mobileSignInOpen }">
          <div
            class="MobileNav_Items">
            <a
              class="MobileNav_Item"
              v-for="mobileItem in mobileItems"
              v-track-click="{
                position: 'mobile-header',
                name: mobileItem.text,
                path: $route.path
              }"
              :href="mobileItem.href"
              :key="mobileItem.text">
              {{ mobileItem.text }}
            </a>
          </div>

          <div
            class="MobileNav_SubActions">
            <div
              v-if="signIn"
              class="MobileNav_SignIn"
              key="TH-MobileNav-SignIn">
              <BaseButton
                block
                theme="ghost"
                :no-hover="true"
                @click="mobileSignInOpen = !mobileSignInOpen">
                SIGN IN
              </BaseButton>
              <SignInForm
                class="MobileNav_SignIn_Form"
                :block-button="true"
              />
            </div>

            <div
              v-if="primaryCta"
              class="MobileNav_CTA"
              key="TH-MobileNav-PrimaryCTA">
              <BaseButton
                block
                theme="orange"
                track-name="primary-cta"
                track-position="header-nav"
                :no-hover="true"
                :no-radius="true"
                :href="primaryCta.href">
                {{ primaryCta.text }}
              </BaseButton>
            </div>
          </div>
        </div>
      </transition>

      <span
        v-show="!isMobile"
        class="TheHeader_Leading_Nav_Items">
        <a
          v-track-click="{
            position: 'header',
            name: desktopLeadingItem.text,
            path: $route.path
          }"
          v-for="desktopLeadingItem in desktopLeadingItems"
          class="TheHeader_Nav_Item"
          :href="desktopLeadingItem.href"
          :key="desktopLeadingItem.text">
          {{ desktopLeadingItem.text }}
        </a>
      </span>
    </div>

    <div
      class="TheHeader_Trailing">
      <span
        v-show="!isMobile">
        <a
          v-track-click="{
            position: 'header',
            name: desktopTrailingItem.text
          }"
          v-for="desktopTrailingItem in desktopTrailingItems"
          class="TheHeader_Nav_Item"
          :href="desktopTrailingItem.href"
          :key="desktopTrailingItem.text">
          {{ desktopTrailingItem.text }}
        </a>
      </span>

      <div
        v-if="signIn && !isMobile"
        class="TheHeader_Nav_Item hasDropdown TheHeader_Nav_Item-sign-in"
        key="TH-Sign-In">
        <i
          v-track-click="{
            position: 'header',
            name: 'sign-in'
          }"
          class="_icon icon-user-profile-avatar-man-male">
        </i>
        <div
         class="SignInDropdown">
         <SignInForm/>
        </div>
      </div>

      <BaseButton
        v-if="secondaryCta"
        class="TheHeader_Nav_CTA TheHeader_Nav_SecondaryCTA"
        :href="secondaryCta.href"
        key="TH-Secondary-CTA">
        {{ secondaryCta.text }}
      </BaseButton>

      <BaseButton
        v-if="primaryCta"
        track-name="primary-cta"
        track-position="header-nav"
        v-track-click="{
          name: 'primary-cta',
          position: 'header',
          path: $route.path
        }"
        class="TheHeader_Nav_CTA TheHeader_Nav_PrimaryCTA"
        theme="orange"
        :href="primaryCta.href"
        key="TH-Primary-CTA">
        {{ primaryCta.text }}
      </BaseButton>

      <button
        v-show="isMobile"
        class="TheHeader_Nav_Item TheHeader_Nav_Item-mobile-menu-toggle"
        @click="toggleMobileNav">
        <i
          class="_icon icon-grid">
        </i>
      </button>
    </div>
  </header>
</template>

<script>
import BaseButton from '@/components/BaseButton.vue';
import SignInForm from '@/forms/SignInForm.vue';

export default {
  name: 'TheHeader',

  components: {
    BaseButton,
    SignInForm,
  },

  data() {
    return {
      mobileNavOpen: false,
      mobileSignInOpen: false,
    };
  },

  props: {
    windowWidth: {
      type: Number,
      default: () => window.innerWidth,
    },
    desktopLeadingItems: {
      type: Array,
      default: () => [],
    },
    desktopTrailingItems: {
      type: Array,
      default: () => [],
    },
    mobileItems: {
      type: Array,
      default: () => [],
    },
    signIn: {
      type: Boolean,
      default: true,
    },
    secondaryCta: {
      type: Object,
      default: null,
    },
    primaryCta: {
      type: Object,
      default: () => ({
        text: 'TRY FREE',
        href: 'https://www.domo.com/start/appstore',
      }),
    },
    goMobile: {
      type: Number,
      default: 700,
    },
  },

  computed: {
    isMobile() {
      return this.windowWidth <= this.goMobile;
    },
  },

  watch: {
    isMobile(isMobile) {
      if (!isMobile && this.mobileNavOpen) {
        this.mobileSignInOpen = false;
        this.mobileNavOpen = false;
      }
    },
    mobileNavOpen(mobileNavOpen) {
      if (!mobileNavOpen && this.mobileSignInOpen) {
        this.mobileSignInOpen = false;
      }
    },
  },

  methods: {
    toggleMobileNav() {
      this.mobileNavOpen = !this.mobileNavOpen;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

$nav-item-arrow-size: 10px;

.TheHeader {
  z-index: 2;
  position: fixed;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  top: 0;
  left: 0;
  width: 100%;
  height: $header-height;
  font-size: 16px;
  white-space: nowrap;
  background-color: $white;
  box-shadow: 0 8px 24px -4px rgba($black, .2);
}

.TheHeader_Logo {
  z-index: 1;
  display: inline-block;
  position: relative;
  width: $header-height;
  height: $header-height;
  vertical-align: top;
  background-color: #99CCEE;
  cursor: pointer;
}

.TheHeader_Logo_Domo {
  padding-top: $header-height / 3;
  transition: transform .3s ease 0s;
}

.TheHeader_Nav_Item {
  display: inline-block;
  position: relative;
  height: $header-height;
  padding: 0 14px;
  line-height: $header-height;
  cursor: pointer;
  ._icon {
    font-size: 24px;
    line-height: $header-height;
  }
  &.hasDropdown:after {
    content: '';
    z-index: 1;
    display: none;
    position: absolute;
    left: 0;
    right: 0;
    bottom: -$nav-item-arrow-size;
    width: $nav-item-arrow-size;
    margin: 0 auto;
    border-left: $nav-item-arrow-size solid transparent;
    border-right: $nav-item-arrow-size solid transparent;
    border-top: $nav-item-arrow-size solid $white;
  }
  &:hover {
    color: #99CCEE;
  }
  &:hover:after {
    display: block;
  }
}

.TheHeader_Trailing {
  padding-right: 16px;
  > * {
    vertical-align: middle;
  }
}

.TheHeader_Nav_Item-sign-in {
  &:hover .SignInDropdown {
    display: block;
  }
}

.SignInDropdown {
  display: none;
  position: fixed;
  top: $header-height;
  right: 16px;
  width: 400px;
  padding: 32px;
  color: $white;
  border-radius: 0 0 6px 6px;
  white-space: normal;
  background-color: #222222;
  box-shadow: 0px 24px 32px -4px rgba($black, .5);
}

.TheHeader_Nav_CTA {
  margin-left: 8px;
  margin-right: 8px;
}

.TheHeader_Nav_Item-mobile-menu-toggle {
  margin-right: -8px;
  transition: color .3s ease 0s;
  &:hover {
    color: inherit;
  }
}

// MOBILE NAV
.MobileNav {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-top: $header-height;
  color: $white;
  background-color: $light-blue;
}

.MobileNav_Items {
  padding-top: 40px;
  padding-left: 20px;
}

.MobileNav_Item {
  display: block;
  padding-left: 16px;
  font-size: 14px;
  font-weight: bold;
  line-height: 50px;
  text-transform: uppercase;
  &:not(:last-child) {
    border-bottom: 1px solid rgba($black, .08);
  }
}

.MobileNav_SubActions {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
}

.MobileNav_SignIn {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background-color: $dark;
  transform: translateY(-80px);
  transition: transform .3s ease 0s;
}

.MobileNav_SignIn_Form {
  padding: 20px;
  padding-top: 8px;
  white-space: normal;
}

.MobileNav_CTA {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background-color: $dark;
  transform: translateY(-40px);
  transition: transform .3s ease 0s;
}

// MOBILE OPEN STATES
.TheHeader.mobileNavOpen {
  color: $white;
  .TheHeader_Logo_Domo {
    transform: translateX(20px);
  }
  .TheHeader_Nav_CTA {
    z-index: -1;
  }
}

// MOBILE SIGN IN OPEN STATES
.MobileNav.mobileSignInOpen {
  .MobileNav_SignIn {
    transform: translateY(-100%);
  }
  .MobileNav_CTA {
    transform: translateY(100%);
  }
}
</style>
